import { historyDiff } from './copyHistoryDiff';
import { Caption, Label } from './styles';
import Pill from '../shared/Pill';
import { formatDateTime } from '../shared/format';

import Button from '@/components/Button';
import PermissionComponent from '@/routes/PermissionComponent';
import { CopyDetail, CopyHistoryItem } from '@/services/lifecycleAPI';

const note = (item: CopyHistoryItem): string => {
  if (item.reset) return 'Reset to default';
  if (item.reverted_from_version !== null) {
    return `Reverted to version ${item.reverted_from_version}`;
  }
  return '';
};

type Props = {
  detail: CopyDetail;
  readOnly: boolean;
  busy: boolean;
  onRevert: (version: number) => void;
};

const CopyHistory = ({ detail, readOnly, busy, onRevert }: Props) => {
  const { history } = detail;
  if (history.length === 0) {
    return (
      <div style={{ padding: '16px 20px' }} data-testid="copyHistory">
        <Caption>
          No publishes yet. This message uses the default text from the code.
        </Caption>
      </div>
    );
  }
  return (
    <div
      data-testid="copyHistory"
      style={{
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {history.map((item, index) => (
        <div
          key={item.version}
          data-testid={`copyHistoryItem-${item.version}`}
          style={{
            border: '1px solid #DADADA',
            borderRadius: 8,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px 12px',
              alignItems: 'center',
            }}
          >
            <strong>{`Version ${item.version}`}</strong>
            {index === 0 && <Pill tone="green">Current</Pill>}
            <Label>{`${item.actor_name ?? 'Unknown'} · ${formatDateTime(item.published_at)}`}</Label>
            <Caption>{note(item)}</Caption>
            {index > 0 && (
              <span
                style={{ marginLeft: 'auto' }}
                title={readOnly ? 'Tenant admin only' : undefined}
              >
                <PermissionComponent
                  id="btn-lifecycle-copy-publish"
                  showAsReadOnly
                >
                  <Button
                    a11yLabel={`Revert to version ${item.version}`}
                    label="Revert to this version"
                    appearance="outline"
                    data-testid={`copyRevertBtn-${item.version}`}
                    disabled={readOnly || busy}
                    onClick={() => onRevert(item.version)}
                  />
                </PermissionComponent>
              </span>
            )}
          </div>
          {item.reset ? (
            <Caption>Default text from the code</Caption>
          ) : (
            historyDiff(detail, index).map((row) => (
              <div
                key={row.key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  fontSize: 13,
                }}
              >
                <Label style={{ fontWeight: 600 }}>{row.label}</Label>
                <span
                  style={{
                    background: '#FFEFEC',
                    color: '#8A0208',
                    borderRadius: 4,
                    padding: '4px 8px',
                    textDecoration: 'line-through',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {row.before || '(empty)'}
                </span>
                <span
                  style={{
                    background: '#E7F4E6',
                    color: '#0F5222',
                    borderRadius: 4,
                    padding: '4px 8px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {row.after}
                </span>
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  );
};

export default CopyHistory;
